import { Navigate, Outlet } from "react-router";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import {
  CARRIERS_REP_DIRECTORY,
  CONSULTANT_VIEW,
} from "@platform/feature-flags";
import { RouteConfig } from "@platform/types";

import { ROUTE_NAMES } from "../constants";
import {
  AddBroker,
  BrokerDashboard,
  CarriersOrder,
  Client,
  ClientSelection,
  Clients,
  ConsultingHome,
  ManageUsers,
  PageNotFound,
  Profile,
  RFP,
  RFPs,
  StewardshipReport,
} from "../pages";
import { AnalystsPage } from "../pages/Analysts";
import { BBEPage } from "../pages/BBE";
import { BBEBroker } from "../pages/BBE/bbeBroker";
import { BrokerSettings } from "../pages/BBE/brokerSettings";
import { CarrierBonusDetail } from "../pages/BBE/carrierBonusDetail";
import { Forecast } from "../pages/BBE/forecast";
import { MonthlyProgress } from "../pages/BBE/monthlyProgress";
import { ScenarioDetails } from "../pages/BBE/scenarioDetails";
import { ScenarioModeling } from "../pages/BBE/scenarioModeling";
import { BrokerPartner } from "../pages/Brokers/brokerPartner";
import { CarriersRepDirectoryPage } from "../pages/CarriersRepDirectory";
import { ManageBrokers } from "../pages/Brokers/manageBrokers";
import { BrokerPartnerClients } from "../pages/Brokers/tabs/clients";
import { Configuration as BrokerPartnerConfiguration } from "../pages/Brokers/tabs/configuration";
import { ConnectedCarriers as BrokerPartnerConnectedCarriers } from "../pages/Brokers/tabs/connectedCarriers";
import { DraftRfps as BrokerPartnerDraftRfps } from "../pages/Brokers/tabs/draftRfps";
import { SubmittedRfps as BrokerPartnerSubmittedRfps } from "../pages/Brokers/tabs/submittedRfps";
import { Users as BrokerPartnerUsers } from "../pages/Brokers/tabs/users";
import { Details as ClientDetails } from "../pages/Clients/details";
import { Rfps as ClientRfps } from "../pages/Clients/rfps";
import {
  ConfigurationEditor,
  ConfigurationsList,
} from "../pages/Configurations";
import { DocumentLibraryPage } from "../pages/DocumentLibrary";
import { DocumentChecklist } from "../pages/RFP/steps/documents/documentChecklist";
import { BrokerRFPDetails } from "../pages/RFPDetails";
import { ValidateCoverageRoute } from "../pages/RFPDetails/components/Build2";
import CarrierTracker from "../pages/RFPDetails/components/CarrierTracker/main";
import { FinalDecisions } from "../pages/RFPDetails/components/FinalDecisions/FinalDecisions";
import { PresentWrapper } from "../pages/RFPDetails/components/Present";
import { Comps } from "../pages/RFPDetails/components/Present/tabs/Comps";
import { Financial } from "../pages/RFPDetails/components/Present/tabs/Financial";
import { Quotes } from "../pages/RFPDetails/components/Present/tabs/Quotes";
import { Responses } from "../pages/RFPDetails/components/Present/tabs/Responses";
import { RfpDetailsView } from "../pages/RFPDetails/components/rfpDetailsView";
import { RumiLaunchpadRoute } from "../pages/RFPDetails/components/RumiLaunchpad";
import Workbench from "../pages/RFPDetails/components/workbench";
import CensusTab from "../pages/RFPDetails/components/censusTab";
import WorkbenchDeliverables from "../pages/RFPDetails/components/workbenchDeliverables";

interface CreateRfpDetailsRoutesOptions {
  enablePropelx?: boolean;
  enableRumiLaunchpad?: boolean;
}

function ConsultingPortalRoute({
  children,
}: {
  children: React.JSX.Element;
}): React.JSX.Element {
  const consultantViewEnabled = useFeatureIsOn(CONSULTANT_VIEW);

  if (!consultantViewEnabled) {
    return <PageNotFound />;
  }

  return children;
}

function ConsultingHomeRoute(): React.JSX.Element {
  return (
    <ConsultingPortalRoute>
      <ConsultingHome />
    </ConsultingPortalRoute>
  );
}

function ConsultingBrokerDashboardRoute(): React.JSX.Element {
  return (
    <ConsultingPortalRoute>
      <BrokerDashboard />
    </ConsultingPortalRoute>
  );
}

function ConsultingStewardshipReportRoute(): React.JSX.Element {
  return (
    <ConsultingPortalRoute>
      <StewardshipReport />
    </ConsultingPortalRoute>
  );
}

function CarriersRepDirectoryRoute(): React.JSX.Element {
  const carriersRepDirectoryEnabled = useFeatureIsOn(CARRIERS_REP_DIRECTORY);

  if (!carriersRepDirectoryEnabled) {
    return <PageNotFound />;
  }

  return <CarriersRepDirectoryPage />;
}

const COMMON_ROUTES: RouteConfig[] = [
  {
    path: ROUTE_NAMES.CLIENTS,
    element: <Clients />,
    // broker_analyst gets the broker-scoped clients view, like agent.
    roles: ["broker", "agent", "broker_analyst"],
  },
  {
    path: ROUTE_NAMES.CLIENTS,
    element: <Clients />,
    roles: ["admin", "analyst", "consultant"],
  },
  {
    path: `${ROUTE_NAMES.CLIENTS}/:id`,
    element: <Client />,
    roles: [
      "admin",
      "analyst",
      "broker",
      "agent",
      "consultant",
      "broker_analyst",
    ],
    children: [
      {
        path: "",
        element: <ClientDetails />,
      },
      {
        path: ROUTE_NAMES.RFPS,
        element: <ClientRfps />,
      },
    ],
  },
  {
    path: ROUTE_NAMES.RFPS,
    element: <RFPs />,
    roles: [
      "admin",
      "analyst",
      "broker",
      "agent",
      "consultant",
      "broker_analyst",
    ],
  },
  {
    path: ROUTE_NAMES.BROKERS,
    element: <ManageBrokers />,
    roles: ["admin", "consultant"],
  },
  {
    path: ROUTE_NAMES.CONSULTING,
    element: <ConsultingHomeRoute />,
    roles: ["admin", "consultant"],
  },
  {
    path: `${ROUTE_NAMES.CONSULTING}/:id`,
    element: <ConsultingBrokerDashboardRoute />,
    roles: ["admin", "consultant"],
  },
  {
    path: `${ROUTE_NAMES.CONSULTING}/:id/stewardship-report`,
    element: <ConsultingStewardshipReportRoute />,
    roles: ["admin", "consultant"],
  },
  {
    path: ROUTE_NAMES.ANALYSTS,
    element: <AnalystsPage />,
    roles: ["admin"],
  },
  {
    path: ROUTE_NAMES.MP_CONFIGS,
    element: <ConfigurationsList />,
    roles: ["admin"],
  },
  {
    path: `${ROUTE_NAMES.MP_CONFIGS}/:id`,
    element: <ConfigurationEditor />,
    roles: ["admin"],
  },
  {
    path: ROUTE_NAMES.CARRIERS_REP_DIRECTORY,
    element: <CarriersRepDirectoryRoute />,
    roles: ["admin"],
  },
  {
    path: ROUTE_NAMES.PRE_REGISTER,
    element: <AddBroker />,
    roles: ["admin", "analyst", "broker"],
  },
  {
    path: ROUTE_NAMES.CARRIERS_ORDER,
    element: <CarriersOrder />,
    roles: ["admin", "analyst", "consultant"],
  },
  {
    path: `${ROUTE_NAMES.BROKERS}/:id`,
    element: <BrokerPartner />,
    roles: ["admin", "analyst", "consultant"],
    children: [
      {
        path: "",
        element: <BrokerPartnerUsers />,
      },
      {
        path: ROUTE_NAMES.CLIENTS,
        element: <BrokerPartnerClients />,
      },
      {
        path: ROUTE_NAMES.BROKER_PARTNER_SUBMITTED_RFPS,
        element: <BrokerPartnerSubmittedRfps />,
      },
      {
        path: ROUTE_NAMES.BROKER_PARTNER_DRAFT_RFPS,
        element: <BrokerPartnerDraftRfps />,
      },
      {
        path: ROUTE_NAMES.BROKER_PARTNER_CONNECTED_CARRIERS,
        element: <BrokerPartnerConnectedCarriers />,
      },
      {
        path: ROUTE_NAMES.BROKER_PARTNER_CONFIGURATION,
        element: <BrokerPartnerConfiguration />,
      },
    ],
  },
  {
    path: ROUTE_NAMES.USERS,
    element: <ManageUsers />,
    roles: ["broker"],
  },
  {
    path: ROUTE_NAMES.PROFILE,
    element: <Profile />,
    roles: [
      "admin",
      "analyst",
      "broker",
      "agent",
      "consultant",
      "broker_analyst",
    ],
  },
  {
    path: ROUTE_NAMES.RFP,
    element: <RFP />,
    // broker_analyst submits RFPs through the same wizard as agent.
    roles: ["broker", "agent", "broker_analyst"],
  },
  {
    path: ROUTE_NAMES.CLIENT_SELECTION,
    element: <ClientSelection />,
    roles: ["broker", "agent", "broker_analyst"],
  },
  {
    path: ROUTE_NAMES.DOCUMENT_CHECKLIST,
    element: <DocumentChecklist />,
    roles: ["broker", "agent", "broker_analyst"],
  },
  {
    path: ROUTE_NAMES.BBE,
    element: <BBEPage />,
    roles: ["admin", "consultant"],
  },
  {
    path: `${ROUTE_NAMES.BBE}/:id`,
    element: <BBEBroker />,
    roles: ["admin", "consultant"],
    children: [
      {
        path: "",
        element: <Forecast />,
      },
      {
        path: ROUTE_NAMES.BBE_MONTHLY_PROGRESS,
        element: <MonthlyProgress />,
      },
      {
        path: ROUTE_NAMES.BBE_FORECAST,
        element: <Forecast />,
      },
      {
        path: ROUTE_NAMES.BBE_SCENARIO_MODELING,
        element: <ScenarioModeling />,
      },
      {
        path: `${ROUTE_NAMES.BBE_SCENARIO_MODELING}/:modelId/:scenarioId`,
        element: <ScenarioDetails />,
      },
      {
        path: `${ROUTE_NAMES.BBE_FORECAST}/${ROUTE_NAMES.BBE_BROKER_SETTINGS}`,
        element: <BrokerSettings />,
      },
      {
        path: `${ROUTE_NAMES.BBE_FORECAST}/${ROUTE_NAMES.BBE_CARRIER_BONUS_DETAILS}/:carrierId`,
        element: <CarrierBonusDetail />,
      },
    ],
  },
];

const createRfpDetailsV1Routes = (
  options: CreateRfpDetailsRoutesOptions = {},
): RouteConfig[] => {
  const { enablePropelx = false, enableRumiLaunchpad = false } = options;

  const routes: RouteConfig[] = [
    {
      path: `${ROUTE_NAMES.RFPS}/:id`,
      element: <BrokerRFPDetails />,
      roles: ["broker", "agent"],
    },
    {
      path: `${ROUTE_NAMES.RFPS}/:id`,
      element: <BrokerRFPDetails isAdmin />,
      // broker_analyst sees the analyst RFP detail view (build, submit
      // to carriers, record final decisions). Broker scoping is
      // enforced server-side. See ADR 10.19.
      roles: ["admin", "analyst", "consultant", "broker_analyst"],
      children: [
        {
          path: ROUTE_NAMES.RFP_CARRIER_TRACKER,
          element: <CarrierTracker />,
        },
        {
          path: "",
          element: <Workbench />,
          children: [
            // With the Launchpad on, an RFP opens to it, and Details
            // moves to a path of its own.
            ...(enableRumiLaunchpad
              ? [
                  {
                    path: "",
                    element: (
                      <Navigate
                        to={ROUTE_NAMES.RFP_WORKBENCH_LAUNCHPAD}
                        replace
                      />
                    ),
                  },
                  {
                    path: ROUTE_NAMES.RFP_WORKBENCH_LAUNCHPAD,
                    element: <RumiLaunchpadRoute />,
                  },
                  {
                    path: ROUTE_NAMES.RFP_WORKBENCH_DETAILS,
                    element: <RfpDetailsView />,
                  },
                ]
              : [
                  {
                    path: "",
                    element: <RfpDetailsView />,
                  },
                ]),
            {
              path: ROUTE_NAMES.RFP_WORKBENCH_CENSUS,
              element: <CensusTab />,
            },
            {
              path: ROUTE_NAMES.RFP_WORKBENCH_TRACK,
              element: <CarrierTracker />,
            },
            ...(enablePropelx
              ? [
                  {
                    path: ROUTE_NAMES.RFP_WORKBENCH_PRESENT,
                    element: <PresentWrapper />,
                    children: [
                      {
                        path: "",
                        element: <Responses />,
                      },
                      {
                        path: ROUTE_NAMES.RFP_WORKBENCH_PRESENT_RESPONSES,
                        element: <Responses />,
                      },
                      {
                        path: ROUTE_NAMES.RFP_WORKBENCH_PRESENT_COMPS,
                        element: <Comps />,
                      },
                      {
                        path: ROUTE_NAMES.RFP_WORKBENCH_PRESENT_FINANCIAL,
                        element: <Financial />,
                      },
                      {
                        path: ROUTE_NAMES.RFP_WORKBENCH_PRESENT_QUOTES,
                        element: <Quotes />,
                      },
                    ],
                  },
                ]
              : []),
            {
              path: ROUTE_NAMES.RFP_WORKBENCH_DELIVERABLES,
              element: <WorkbenchDeliverables />,
            },
            {
              path: ROUTE_NAMES.RFP_WORKBENCH_FINAL,
              element: <FinalDecisions />,
            },
            {
              path: ROUTE_NAMES.RFP_WORKBENCH_BUILD_2,
              element: <Outlet />,
              children: [
                {
                  path: "",
                  element: <DocumentLibraryPage />,
                },
                {
                  path: `${ROUTE_NAMES.RFP_WORKBENCH_BUILD_2_FORMS}/:planName?`,
                  element: <ValidateCoverageRoute />,
                },
              ],
            },
          ],
        },
      ],
    },
  ];

  return routes;
};

const RFP_DETAILS_V2: RouteConfig[] = [
  {
    path: `${ROUTE_NAMES.RFPS}/:id`,
    element: <BrokerRFPDetails />,
    roles: ["broker", "agent"],
    children: [
      {
        path: "",
        element: <RfpDetailsView />,
      },
    ],
  },
];

const createRoutes = (rfpDetailsConfig: RouteConfig[]): RouteConfig[] => [
  ...COMMON_ROUTES,
  ...rfpDetailsConfig,
];

// Final route exports
export const APP_ROUTES = createRoutes(createRfpDetailsV1Routes());
export const APP_ROUTES_V2 = createRoutes(RFP_DETAILS_V2);

// Brokers and agents get the V2 RFP details tree; every other role gets the
// analyst workbench (V1) tree.
export const getRoutes = (
  isBrokerOrAgent: boolean,
  options: CreateRfpDetailsRoutesOptions = {},
): RouteConfig[] =>
  isBrokerOrAgent
    ? APP_ROUTES_V2
    : createRoutes(createRfpDetailsV1Routes(options));
